/*
  "Shipping to your ZIP" in the cart (David, 2026-10-05: shipping only shows at checkout, after the buyer types their
  email and address, and that's where carts are lost; "maybe we can generate an estimate for them"). The buyer types a
  US ZIP code; we ask Shopify for the same live USPS, UPS and FedEx rates checkout will show (the cart's real weight),
  and show the cheapest. Nothing is tracked: the ZIP stays in this browser (localStorage) so the next cart can reuse it.
  Shopify needs the state for live rates, so the state comes from the ZIP's first three digits (USPS ranges).
  US territories, the Freely Associated States and military mail are "states" of the US zone in Shopify (David: "of
  course we ship to Puerto Rico... we will ship anywhere"), so their ZIPs get live quotes too.
*/
(() => {
  if (customElements.get('pa-ship-estimate')) return;

  // [first ZIP3, last ZIP3, state] (USPS prefixes). Territories and military mail are handled by territoryFor below.
  const ZIP3 = [
    [5, 5, 'NY'], [10, 27, 'MA'], [28, 29, 'RI'], [30, 38, 'NH'], [39, 49, 'ME'], [50, 54, 'VT'], [55, 55, 'MA'],
    [56, 59, 'VT'], [60, 69, 'CT'], [70, 89, 'NJ'], [100, 149, 'NY'], [150, 196, 'PA'], [197, 199, 'DE'], [200, 200, 'DC'],
    [201, 201, 'VA'], [202, 205, 'DC'], [206, 219, 'MD'], [220, 246, 'VA'], [247, 268, 'WV'], [270, 289, 'NC'],
    [290, 299, 'SC'], [300, 319, 'GA'], [320, 339, 'FL'], [341, 349, 'FL'], [350, 369, 'AL'], [370, 385, 'TN'],
    [386, 397, 'MS'], [398, 399, 'GA'], [400, 427, 'KY'], [430, 459, 'OH'], [460, 479, 'IN'], [480, 499, 'MI'],
    [500, 528, 'IA'], [530, 549, 'WI'], [550, 567, 'MN'], [569, 569, 'DC'], [570, 577, 'SD'], [580, 588, 'ND'],
    [590, 599, 'MT'], [600, 629, 'IL'], [630, 658, 'MO'], [660, 679, 'KS'], [680, 693, 'NE'], [700, 714, 'LA'],
    [716, 729, 'AR'], [730, 732, 'OK'], [733, 733, 'TX'], [734, 749, 'OK'], [750, 799, 'TX'], [800, 816, 'CO'],
    [820, 831, 'WY'], [832, 838, 'ID'], [840, 847, 'UT'], [850, 865, 'AZ'], [870, 884, 'NM'], [885, 885, 'TX'],
    [889, 898, 'NV'], [900, 961, 'CA'], [967, 968, 'HI'], [970, 979, 'OR'], [980, 994, 'WA'], [995, 999, 'AK'],
  ];
  const STATE = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut',
    DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
    IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
    MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
    NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
    NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
    RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah',
    VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  };
  // Territories, Freely Associated States and military mail (Shopify accepts their two-letter codes as US states).
  const territoryFor = (zip) => {
    const p3 = parseInt(zip.slice(0, 3), 10), n = parseInt(zip, 10);
    if (p3 === 6 || p3 === 7 || p3 === 9) return 'PR';
    if (p3 === 8) return 'VI';
    if (p3 >= 90 && p3 <= 98) return 'AE';
    if (p3 === 340) return 'AA';
    if (p3 >= 962 && p3 <= 966) return 'AP';
    if (n === 96799) return 'AS';
    if (n >= 96950 && n <= 96952) return 'MP';
    if (n >= 96939 && n <= 96940) return 'PW';
    if (n >= 96941 && n <= 96944) return 'FM';
    if (n >= 96960 && n <= 96970) return 'MH';
    if (p3 === 969) return 'GU';
    return null;
  };
  const stateFor = (zip) => {
    const t = territoryFor(zip);
    if (t) return t;
    const n = parseInt(zip.slice(0, 3), 10);
    const hit = ZIP3.find(([a, b]) => n >= a && n <= b);
    return hit ? STATE[hit[2]] : null;
  };
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  const KEY = 'pa-ship-zip';
  const TEXT_US = 'Text us at <a href="sms:+14157611328">(415) 761-1328</a> and we’ll quote it.';

  class PaShipEstimate extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.input = this.querySelector('input');
      this.button = this.querySelector('button');
      this.out = this.querySelector('.pa-ship__out');
      if (!this.input || !this.button || !this.out) return;
      this.button.addEventListener('click', () => this.run(false));
      this.input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this.run(false); } });
      this.input.addEventListener('input', () => { this.input.value = this.input.value.replace(/\D/g, '').slice(0, 5); });
      let zip = '';
      try { zip = localStorage.getItem(KEY) || ''; } catch (e) {}
      if (/^\d{5}$/.test(zip)) { this.input.value = zip; this.run(true); }
    }

    show(html) { this.out.innerHTML = html; this.out.hidden = !html; }

    async run(quiet) {
      const zip = (this.input.value || '').trim();
      if (!/^\d{5}$/.test(zip)) { if (!quiet) this.show('Enter a 5-digit US ZIP code.'); return; }
      try { localStorage.setItem(KEY, zip); } catch (e) {}
      const state = stateFor(zip);
      if (!state) { this.show('We can’t quote ' + esc(zip) + ' here. ' + TEXT_US); return; }
      const token = (this._seq = (this._seq || 0) + 1);
      this.button.disabled = true;
      this.show('Checking shipping to ' + esc(zip) + '…');
      const q = 'shipping_address%5Bzip%5D=' + zip + '&shipping_address%5Bcountry%5D=United%20States&shipping_address%5Bprovince%5D=' + encodeURIComponent(state);
      let rates = null;
      try {
        const prep = await fetch('/cart/prepare_shipping_rates.json?' + q, { method: 'POST' });
        if (prep.ok || prep.status === 202) {
          for (let i = 0; i < 12 && rates === null; i++) {
            await new Promise((r) => setTimeout(r, i ? 1200 : 500));
            const r = await fetch('/cart/async_shipping_rates.json?' + q);
            if (token !== this._seq) return;
            if (r.status === 200) { rates = ((await r.json()).shipping_rates) || []; }
            else if (r.status !== 202) break;
          }
        }
      } catch (e) {}
      if (token !== this._seq) return;
      this.button.disabled = false;
      if (rates === null) { this.show('We couldn’t get a quote right now. You’ll see shipping at checkout.'); return; }
      if (!rates.length) { this.show('No shipping quote for ' + esc(zip) + '. ' + TEXT_US); return; }
      const cheapest = rates.reduce((a, b) => (parseFloat(b.price) < parseFloat(a.price) ? b : a));
      let price = '$' + parseFloat(cheapest.price).toFixed(2);
      try { price = new Intl.NumberFormat('en-US', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(parseFloat(cheapest.price)); } catch (e) {}
      this.show('Shipping to ' + esc(zip) + ': from <strong>' + esc(price) + '</strong> (' + esc(cheapest.name) + '). Faster options at checkout.');
    }
  }
  customElements.define('pa-ship-estimate', PaShipEstimate);
})();
